class SubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.cartForm = this.closest('cart-form');
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  connectedCallback() {
    if (this.cartForm) {
      this.observeCartFormChanges();
    }
    this.initOptions();
    this.initDropdown();
    this.initQuantity();
    this.initDefaultSelection();
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  initOptions() {
    const options = this.querySelectorAll('.subs-button');
    options.forEach(option => {
      option.addEventListener('click', () => this.selectOption(option));
    });
  }

  // Subscribe & Save carries `selected-plan` in the markup so there's no flash of
  // the wrong row, but the cart form only learns about the selling plan when an
  // option is actually selected. Replay that selection once cart-form has upgraded
  // and written its item, otherwise the widget would show a subscription while
  // adding a one-time purchase.
  initDefaultSelection() {
    const preselected = this.querySelector('.subs-button.selected-plan');
    if (!preselected) return;

    customElements
      .whenDefined('cart-form')
      .then(() => this.selectOption(preselected))
      .catch(() => {});
  }

  // Both rows price the selected quantity, so the widget follows the same
  // product-card quantity the bottle selector writes to.
  initQuantity() {
    this.productCard = this.closest('product-card');
    if (!this.productCard) {
      this.renderPrices();
      return;
    }

    customElements
      .whenDefined('product-card')
      .then(() => {
        this.productCard.dataStore?.listen('quantity', () => this.renderPrices());
        this.renderPrices();
        this.reapplySelectedPlan();
      })
      .catch(() => this.renderPrices());
  }

  get quantity() {
    const quantity = parseInt(this.productCard?.data?.quantity?.value, 10);
    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  // Quantity-break tiers live on the bottle selector, which renders them from the
  // `product_card_quantity` metaobject. Mirrors Shopify's "best qualifying tier
  // wins", so an off-tier quantity gets the tier below it — 4 bottles pays the
  // 3-bottle discount. Returns cents.
  tierDiscount(quantity) {
    const selector = this.productCard?.querySelector('bottle-quantity-selector');
    if (!selector) return 0;

    return Array.from(selector.querySelectorAll('.bottle-option')).reduce((best, option) => {
      const minimum = parseInt(option.dataset.quantity, 10);
      if (Number.isNaN(minimum) || minimum > quantity) return best;
      return Math.max(best, parseInt(option.dataset.discountAmount, 10) || 0);
    }, 0);
  }

  renderPrices() {
    const quantity = this.quantity;
    const discount = this.tierDiscount(quantity);

    this.querySelectorAll('.subs-button').forEach((button) => {
      const unitPrice = parseInt(button.dataset.unitPrice, 10) || 0;
      const unitCompare = parseInt(button.dataset.unitCompare, 10) || 0;

      const lineTotal = unitPrice * quantity;
      const total = lineTotal - Math.min(discount, lineTotal);
      const compare = Math.max(unitCompare * quantity, lineTotal);
      const savings = compare - total;

      const totalEl = button.querySelector('[data-price-total]');
      const compareEl = button.querySelector('[data-price-compare]');
      const savingsEl = button.querySelector('[data-price-savings]');

      if (totalEl) totalEl.dataset.price = total;

      if (compareEl) {
        compareEl.dataset.price = compare;
        compareEl.closest('[data-price-compare-wrap]')?.classList.toggle('hidden', savings <= 0);
      }

      if (savingsEl) {
        // Percentage off the compare price, rounded the same way as the Liquid in
        // subscription-widget-price that renders the initial value.
        savingsEl.textContent = `${compare > 0 ? Math.round((savings / compare) * 100) : 0}%`;
        savingsEl.closest('[data-price-savings-wrap]')?.classList.toggle('hidden', savings <= 0);
      }
    });
  }

  initDropdown() {
    this.dropdown = this.querySelector('[data-dropdown]');
    if (!this.dropdown) return;

    const toggle = this.dropdown.querySelector('[data-subs-dropdown-toggle]');
    const planOptions = this.dropdown.querySelectorAll('[data-subs-option]');

    toggle?.addEventListener('click', e => {
      e.stopPropagation();
      const isOpen = this.dropdown.getAttribute('data-open') === 'true';
      this.dropdown.setAttribute('data-open', String(!isOpen));
    });

    planOptions.forEach(opt => {
      opt.addEventListener('click', e => {
        e.stopPropagation();
        this.selectPlan(opt);
        this.dropdown.setAttribute('data-open', 'false');
      });
    });
  }

  handleOutsideClick(e) {
    if (!this.dropdown) return;
    if (!this.dropdown.contains(e.target)) {
      this.dropdown.setAttribute('data-open', 'false');
    }
  }

  selectPlan(option) {
    const planId = option.getAttribute('data-plan-id');
    const planName = option.getAttribute('data-plan-name');
    const planIndex = option.getAttribute('data-plan-index');
    const planPrice = option.getAttribute('data-plan-price');
    const planComparePrice = option.getAttribute('data-plan-compare-price');

    const subscribeButton = this.querySelector('.subs-button-subscribe');
    if (!subscribeButton) return;

    subscribeButton.setAttribute('data-plan-id', planId);
    subscribeButton.setAttribute('data-frequency-label', planName);
    subscribeButton.dataset.unitPrice = planPrice;
    subscribeButton.dataset.unitCompare = planComparePrice;

    const labelEl = this.querySelector('[data-subs-current-label]');
    if (labelEl) labelEl.textContent = planName;

    this.renderPrices();

    this.querySelectorAll('[data-subs-points-for]').forEach(el => {
      el.classList.toggle('hidden', el.getAttribute('data-subs-points-for') !== planIndex);
    });

    this.selectOption(subscribeButton);
  }

  selectOption(option) {
    const options = this.querySelectorAll('.subs-button');
    options.forEach(opt => opt.classList.remove('selected-plan'));
    option.classList.add('selected-plan');

    const frequencyLabel = this.querySelector('[data-subs-frequency-label]');
    const label = option.getAttribute('data-frequency-label');
    if (frequencyLabel && label) frequencyLabel.textContent = label;

    this.updateFormData(option);
  }

  updateFormData(option) {
    if (this.cartForm) {
      const rawForm = this.cartForm.getAttribute('data-form');
      if (!rawForm) return;

      const dataForm = JSON.parse(rawForm);
      const items = Object.values(dataForm?.items ?? {});
      const targetItem = items[0];

      if (targetItem) {
        const payload = {
          id: targetItem.id,
          quantity: targetItem.quantity,
          ...(option.getAttribute('data-plan-id') && {
            selling_plan: option.getAttribute('data-plan-id'),
          }),
          properties: {},
        };

        this.cartForm?.formDataAPI?.updateItems(payload);
      }
    }
  }

  observeCartFormChanges() {
    const observer = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type !== 'attributes') continue;

        if (mutation.attributeName === 'data-product-card-selected_variant_id') {
          this.handleCartFormUpdate();
        } else if (mutation.attributeName === 'data-product-card-quantity') {
          this.reapplySelectedPlan();
        }
      }
    });

    observer.observe(this.cartForm.querySelector("#product-card-section"), {
      attributes: true,
      attributeFilter: ['data-product-card-selected_variant_id', 'data-product-card-quantity'],
    });

    window.addEventListener('beforeunload', () => observer.disconnect());
  }

  // product-card rewrites the cart form item as { id, quantity } whenever the
  // quantity changes, and updateFormItems replaces the item wholesale rather than
  // merging — so the selling plan is dropped and the customer silently reverts to a
  // one-time purchase while the widget still shows Subscribe & Save selected.
  //
  // This has to hook the attribute rather than the quantity dataStore event:
  // dispatchChange notifies listeners before it writes the attribute, so a listener
  // would re-apply the plan and then be overwritten. MutationObserver callbacks run
  // at the microtask checkpoint, after the synchronous attributeChangedCallback.
  reapplySelectedPlan() {
    const selected = this.querySelector('.subs-button.selected-plan');
    if (selected) this.updateFormData(selected);
  }

  handleCartFormUpdate() {
    const dataForm = JSON.parse(this.cartForm.getAttribute('data-form'));
    const variantId = Object.values(dataForm.items)?.[0]?.id;

    if (variantId) {
      this.classList.add("loading-plans");
      fetch(`${window.location.pathname}?variant=${variantId}`)
        .then(response => {
          if (!response.ok) throw new Error('Failed to fetch updated section');
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const newDocument = parser.parseFromString(html, 'text/html');
          const newContent = newDocument.querySelector('subs-widget');

          if (newContent) {
            this.replaceWith(newContent);
          }
        })
        .catch(error => {
          console.error('Error updating section:', error);
        });
    }
  }
}
customElements.define('subs-widget', SubscriptionWidget);
